import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, concatMap, from, scan } from 'rxjs';

export interface ArchiveTag {
  id: string;
  name: string;
  text_color: string;
  bg_color: string;
}

export interface Archive {
  id: number | string;
  name: string;
  created_at: string;
  updated_at: string;
  tag?: ArchiveTag | null;
  tags?: ArchiveTag[];
  [key: string]: any;
}

export interface PreviewResponse {
  message: string;
  pages: string[];
  total_pages: number;
  current_page: number;
  page_size: number;
  has_next: boolean;
  has_previous: boolean;
  file_type: string;
}

export interface PaginatedArchivesResponse {
  archives: Archive[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  message?: string;
}

export interface UploadBatchProgress {
  uploaded: number;
  total: number;
  archives: Archive[];
}

@Injectable({ providedIn: 'root' })
export class ArchivesService {
  private baseUrl = '/api/archives/';

  private readonly UPLOAD_BATCH_SIZE = 10;

  constructor(private http: HttpClient) {}

  list(tag?: string, search?: string, page: number = 1, pageSize: number = 10, ext?: string): Observable<PaginatedArchivesResponse> {
    let url = `${this.baseUrl}?page=${page}&page_size=${pageSize}`;
    if (tag) {
      url += `&tag=${encodeURIComponent(tag)}`;
    }
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (ext) {
      url += `&ext=${encodeURIComponent(ext)}`;
    }
    return this.http.get<PaginatedArchivesResponse>(url);
  }

  upload(files: File[], tagIds: string[] = []): Observable<Archive[]> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file, file.name);
    });
    tagIds.forEach(tagId => {
      formData.append('tag_ids', tagId);
    });
    return this.http.post<Archive[]>(this.baseUrl, formData);
  }

  uploadInBatches(files: File[], tagIds: string[] = []): Observable<UploadBatchProgress> {
    const batches: File[][] = [];
    for (let i = 0; i < files.length; i += this.UPLOAD_BATCH_SIZE) {
      batches.push(files.slice(i, i + this.UPLOAD_BATCH_SIZE));
    }

    const total = files.length;
    const initial: UploadBatchProgress = { uploaded: 0, total, archives: [] };

    return from(batches).pipe(
      concatMap(batch => this.upload(batch, tagIds)),
      scan((acc, response) => {
        const archives = this.extractArchives(response);
        return { uploaded: acc.uploaded + archives.length, total, archives: [...acc.archives, ...archives] };
      }, initial)
    );
  }

  private extractArchives(response: any): Archive[] {
    if (Array.isArray(response)) return response;
    if (response?.archives && Array.isArray(response.archives)) return response.archives;
    if (response?.files && Array.isArray(response.files)) return response.files;
    return [];
  }

  delete(id: number | string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}${id}`);
  }

  getPreview(id: number | string, page: number = 1, pageSize: number = 5): Observable<PreviewResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', pageSize.toString());
    
    return this.http.get<PreviewResponse>(`${this.baseUrl}${id}/preview`, { params });
  }

  download(id: number | string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}${id}/download`, { responseType: 'blob' });
  }
}